import { GraphNode } from '../graph-node.js';
import { reportGraphError } from '../errors.js';
/**
 * UE5-style Reroute: a cosmetic hop in the wire that carries either one exec
 * wire or one value wire. It holds no data of its own — exec triggers are
 * forwarded to the `exec` out port and value reads are pulled straight from the
 * matching in port, so any chain of Reroutes behaves exactly like the direct
 * wire it replaces.
 */
export class RerouteNode extends GraphNode {
    isPullingValue = false;
    isPropagatingDirty = false;
    async run() {
        await this.runOutPort('exec');
    }
    getOutPortData(outPortKey) {
        const incoming = this.inConnections.get(outPortKey)?.[0];
        if (!incoming) {
            this.reportBrokenPull(`has no wire feeding its "${outPortKey}" in port`);
            return undefined;
        }
        if (this.isPullingValue) {
            this.reportBrokenPull('value pull cycle detected - reroute chain loops back on itself');
            return undefined;
        }
        this.isPullingValue = true;
        try {
            return incoming.connectedNode.getOutPortData(incoming.outPortKey);
        }
        finally {
            this.isPullingValue = false;
        }
    }
    /**
     * A Reroute is not a ValueNode and therefore holds no cache, but it must not
     * break the invalidation chain: value nodes behind it would keep serving a
     * stale result after an upstream variable was written.
     */
    markDirty() {
        if (this.isPropagatingDirty)
            return;
        this.isPropagatingDirty = true;
        try {
            this.propagateDirtyToConsumers();
        }
        finally {
            this.isPropagatingDirty = false;
        }
    }
    propagateDirtyToConsumers() {
        for (const connections of this.outConnections.values()) {
            for (const connection of connections)
                connection.connectedNode.markDirty();
        }
    }
    reportBrokenPull(summary) {
        reportGraphError({ kind: 'exec', nodeType: this.type, nodeId: this.id, summary });
    }
}
